import { ArrowRight, Play, X, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { useState, useRef, useEffect } from "react";
import { m as motion, useScroll, useTransform } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogClose,
} from "./ui/dialog";


const HeroSection = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"]
  });

  // X text zoom effect - x scales up slightly at first, then massively to engulf the screen
  const xScale = useTransform(scrollYProgress, [0, 0.1, 0.6], [1, 1.5, 60]);
  // The colored 'x' fades out entirely while the scale is still small, leaving a proper transparent 'X' window
  const coloredXOpacity = useTransform(scrollYProgress, [0, 0.1], [1, 0]);

  // Side text (VR and ED) slide and fade away
  const sideOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);
  const vrOffset = useTransform(scrollYProgress, [0, 0.15], [0, -100]);
  const edOffset = useTransform(scrollYProgress, [0, 0.15], [0, 100]);

  // Video reveal
  const videoOpacity = useTransform(scrollYProgress, [0.1, 0.5], [0, 1]);
  const videoScale = useTransform(scrollYProgress, [0, 0.6], [1.2, 1]);

  // Overlay fades out as text zooms
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.3], [0.92, 0]);

  // Content below text fades in after zoom
  const contentOpacity = useTransform(scrollYProgress, [0.45, 0.75], [0, 1]);
  const contentY = useTransform(scrollYProgress, [0.45, 0.75], [60, 0]);

  // Stats
  const statsOpacity = useTransform(scrollYProgress, [0.65, 0.85], [0, 1]);
  const statsY = useTransform(scrollYProgress, [0.65, 0.85], [40, 0]);

  // Badge and initial content fade out
  const initialContentOpacity = useTransform(scrollYProgress, [0, 0.05], [1, 0]);

  return (
    <>
      {/* Video Demo Modal */}
      < Dialog open={isVideoOpen} onOpenChange={setIsVideoOpen} >
        <DialogContent className="max-w-4xl p-0 bg-background/95 backdrop-blur border-border overflow-hidden">
          <DialogClose className="absolute right-4 top-4 z-10 rounded-full bg-background/80 p-2 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogClose>
          <div className="aspect-video w-full">
            <video
              autoPlay
              controls
              className="w-full h-full object-contain"
              aria-label="VRxED product demo video"
            >
              <source src="/videos/demo.mp4" type="video/mp4" />
              <track kind="captions" srcLang="en" label="English" default />
              Your browser does not support the video tag.
            </video>
          </div>
        </DialogContent>
      </Dialog >

      <section ref={sectionRef} className="relative h-[300vh]">
        {/* Sticky container - stays fixed while scrolling */}
        <div className="sticky top-0 h-screen w-full overflow-hidden">

          {/* Layer 1: Video Background (revealed as text zooms) */}
          <motion.div
            className="absolute inset-0 z-0"
            style={{ scale: videoScale }}
          >
            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
              aria-hidden="true"
              tabIndex={-1}
              poster="/images/hero-poster.webp"
              preload="none"
            >
              <source src="/videos/hero-background.mp4" type="video/mp4" />
              <track kind="captions" srcLang="en" label="English" />
            </video>
          </motion.div>

          {/* Layer 2: Mask overlay that reveals the video as 'X' zooms */}
          {/* Black background masks out the video entirely, while white text (multiply) creates a transparent hole */}
          <motion.div
            className="absolute inset-0 z-[1] bg-black mix-blend-multiply flex items-center justify-center overflow-hidden pointer-events-none"
          >
            <div className="flex items-center whitespace-nowrap select-none font-black tracking-[-0.04em] leading-none text-[22vw] md:text-[20vw] lg:text-[18vw]">
              {/* Invisible spacers matching the side texts */}
              <span className="opacity-0">VR</span>
              <motion.span
                className="text-white"
                style={{ scale: xScale }}
              >
                x
              </motion.span>
              <span className="opacity-0">ED</span>
            </div>
          </motion.div>

          {/* Layer 3: Ambient glow effects */}
          <div className="absolute inset-0 z-[2] pointer-events-none">
            <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-primary/8 rounded-full blur-[120px]" />
            <div className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] bg-primary/5 rounded-full blur-[100px]" />
          </div>

          {/* Layer 4: Visible text layer that rests exactly over the mask */}
          <div className="absolute inset-0 z-[3] flex items-center justify-center overflow-hidden pointer-events-none">
            <div className="flex items-center whitespace-nowrap select-none font-black tracking-[-0.04em] leading-none text-[22vw] md:text-[20vw] lg:text-[18vw]">
              <motion.span
                className="text-foreground"
                style={{ opacity: sideOpacity, x: vrOffset }}
              >
                VR
              </motion.span>
              {/* This colored X fades out early to reveal the transparent hole in the mask underneath */}
              <motion.span
                className="text-primary"
                style={{ scale: xScale, opacity: coloredXOpacity }}
              >
                x
              </motion.span>
              <motion.span
                className="text-foreground"
                style={{ opacity: sideOpacity, x: edOffset }}
              >
                ED
              </motion.span>
            </div>
          </div>

          {/* Layer 5: Video tint overlay for readability (appears as video fills screen) */}
          <motion.div
            className="absolute inset-0 z-[4] bg-background/50 pointer-events-none"
            style={{ opacity: videoOpacity }}
          />

          {/* Layer 6: Initial badge & scroll hint (before scroll) */}
          <motion.div
            className="absolute inset-0 z-[5] flex flex-col items-center justify-between pt-32 pb-12 pointer-events-none"
            style={{ opacity: initialContentOpacity }}
          >
            <motion.div
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full glass-panel shadow-glow pointer-events-auto"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-sm font-medium text-primary">Revolutionizing Healthcare Education</span>
            </motion.div>

            <div className="flex flex-col items-center gap-3">
              <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-medium">Scroll to explore</span>
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
              >
                <ChevronDown className="w-5 h-5 text-primary/60" />
              </motion.div>
            </div>
          </motion.div>

          {/* Layer 7: Content that appears after text zooms through */}
          <motion.div
            className="absolute inset-0 z-[6] flex items-center"
            style={{ opacity: contentOpacity }}
          >
            <div className="container mx-auto px-6">
              <div className="max-w-3xl text-left">
                <motion.h2
                  className="text-5xl md:text-7xl lg:text-8xl font-extrabold mb-6 text-foreground tracking-tight text-balance leading-tight"
                  style={{ y: contentY }}
                >
                  Learn <br className="hidden md:block" />Medicine{" "}
                  <span className="text-gradient">Through VR</span>
                </motion.h2>

                <motion.p
                  className="text-lg md:text-xl text-foreground/70 max-w-2xl mb-10 leading-relaxed"
                  style={{ y: contentY }}
                >
                  Immersive virtual reality training for healthcare professionals.
                  Master anatomy, emergency response, and patient care with cutting-edge simulation technology.
                </motion.p>

                {/* CTA Buttons */}
                <motion.div
                  className="flex flex-col sm:flex-row items-start gap-4"
                  style={{ y: contentY }}
                >
                  <Button variant="glow" size="lg" className="group text-lg px-8 py-6 rounded-full" asChild>
                    <a href="https://learning.vrxed.com/" target="_blank" rel="noopener noreferrer">
                      Start Learning
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                  <Button variant="outline" size="lg" className="group border-foreground/20 text-foreground hover:bg-foreground/10 text-lg px-8 py-6 rounded-full" onClick={() => setIsVideoOpen(true)}>
                    <Play className="mr-2 w-5 h-5" />
                    Watch Demo
                  </Button>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Layer 8: Stats bar at bottom (appears last) */}
          <motion.div
            className="absolute bottom-0 left-0 right-0 z-[7] pb-8"
            style={{ opacity: statsOpacity, y: statsY }}
          >
            <div className="container mx-auto px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 glass-panel rounded-3xl p-6 md:p-8 text-left">
                {[
                  { value: "50+", label: "Partner Universities" },
                  { value: "10K+", label: "Students Trained" },
                  { value: "25+", label: "VR Modules" },
                  { value: "98%", label: "Success Rate" },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="text-2xl md:text-3xl font-bold text-foreground mb-1">{stat.value}</div>
                    <div className="text-xs md:text-sm text-foreground/60">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
